<template>
  <!-- Any files the client wishes to upload -->
  <div
    :id="`file-${identifier}-panel`"
  >
    <div
      v-if="browserSupportsFileInput"
      :key="identifier"
      class="is-clearfix control"
    >
      <transition-group
        name="list"
        tag="div"
      >
        <div
          key="file-placeholder"
          class="file-detail-container is-clipped"
          :style="{position: 'relative'}"
        >
          <input
            ref="file-input"
            :disabled="submitted"
            class="invisible-file-input"
            type="file"
            :multiple="true"
            :filename="value.filename"
            @dragover="isDraggedOver = true"
            @dragleave="isDraggedOver = false"
            @drop="isDraggedOver = false"
            @change="addFile($event)"
          >

          <div
            class="file-placeholder"
            :class="{ 'dragged-over': isDraggedOver }"
          >
            {{ $t('modify_website.form.field4.placeholder') }}
          </div>
        </div>
        <span
          v-if="onlyImages"
          key="images"
        >
          <div
            v-for="(file, fileIndex) in value"
            :key="value[fileIndex].filename"
            class="file-detail-container"
          >
            <div class="image-container">
              <font-awesome-layers
                class="fa-stack fa-icon-stack"
                size="2x"
                :class="{loading: fileIconStatus[file.filename] && fileIconStatus[file.filename].loading}"
                @click="removeFile(file)"
              >
                <span class="image-wrapper">
                  <img
                    v-if="srcImage[file.displayName]"
                    class="img-file"
                    :src="srcImage[file.displayName]"
                  >
                  <font-awesome-icon
                    icon="cloud-upload-alt"
                    color="grey"
                    class="upload-icon"
                  />
                  <font-awesome-icon
                    icon="times"
                    class="delete-icon"
                  />
                </span>
              </font-awesome-layers>
            </div>
            <div
              class="filename-text"
              :class="{
                loading: fileIconStatus[file.filename] && fileIconStatus[file.filename].loading,
                full: fileIconStatus[file.filename] && fileIconStatus[file.filename].showFullText
              }"
              @click="toggleFullTextDisplay(file.filename)"
            >
              <span>{{ file.displayName }}</span>
            </div>
          </div>
        </span>

        <span
          v-else
          key="generic"
        >
          <div
            v-for="(file, fileIndex) in value"
            :key="value[fileIndex].filename"
            class="file-detail-container"
          >
            <font-awesome-layers
              class="fa-stack fa-icon-stack"
              size="2x"
              :class="{loading: fileIconStatus[file.filename] && fileIconStatus[file.filename].loading}"
              @click="removeFile(file)"
            >
              <font-awesome-icon
                icon="file"
                size="2x"
                color="white"
              />
              <font-awesome-icon
                icon="cloud-upload-alt"
                color="grey"
                class="upload-icon"
              />
              <font-awesome-icon
                icon="times"
                class="delete-icon"
              />
            </font-awesome-layers>
            <div
              class="filename-text"
              :class="{
                loading: fileIconStatus[file.filename] && fileIconStatus[file.filename].loading,
                full: fileIconStatus[file.filename] && fileIconStatus[file.filename].showFullText
              }"
              @click="toggleFullTextDisplay(file.filename)"
            >
              <span>{{ file.displayName }}</span>
            </div>
          </div>
        </span>
      </transition-group>
    </div>
    <div
      v-else
      class="form-section"
    >
      {{ $t('browser_compatibility.file_uploads') }}
    </div>
  </div>
  <!-- end of 'files' input section -->
</template>

<script>
import { MEGABYTES_IN_BYTES } from '@/helpers'
import uuid from 'short-uuid'
import modernizr from 'modernizr'
import { mapState } from 'vuex'
export default {

  name: 'FileUploader',
  props: {
    submitted: {
      type: Boolean,
      default: false
    },
    mustDeleteToRemove: {
      type: Boolean,
      default: false
    },
    onlyImages: {
      type: Boolean,
      default: false
    },
    label: {
      type: String,
      default: ''
    },
    identifier: {
      type: Number,
      default: 0
    },
    value: {
      type: [Array, Object],
      default: () => ([])
    }
  },
  data () {
    return {
      // files: [],
      fileIconStatus: {},
      browserSupportsFileInput: false,
      isDraggedOver: false,
      srcImage: {}
    }
  },
  computed: {
    ...mapState(['account'])
  },

  watch: {
    submitted (submitted) {
      // if we don't clear the file-input, we won't be able to attach the same file to
      // the fresh form as on the previous form.
      const freshForm = !submitted
      if (freshForm) {
        this.clearFileInput()
      }
    }
  },

  mounted () {
    this.value.map(file => {
      this.$set(
        this.fileIconStatus,
        file.filename,
        { showFullText: false, loading: false }
      )
    })
  },

  created () {
    // make sure files can't be dropped into the browser outside of the file input field
    window.addEventListener('dragover', this.ignoreDropsOutsideFileInput, false)
    window.addEventListener('drop', this.ignoreDropsOutsideFileInput, false)

    // check if the browser supports fileinput
    this.browserSupportsFileInput = modernizr.fileinput
  },

  beforeDestroy () {
    // remove all files that were uploaded to S3 if the page is
    // navigated away from without submitting
    if (!this.submitted && !this.mustDeleteToRemove) {
      this.removeAllFiles(this.value)
    }

    // clean up by removing listeners (avoid memory leaks)
    window.removeEventListener('dragover', this.ignoreDropsOutsideFileInput)
    window.removeEventListener('drop', this.ignoreDropsOutsideFileInput)
  },

  methods: {
    toggleFullTextDisplay (filename) {
      this.fileIconStatus[filename].showFullText = !this.fileIconStatus[filename].showFullText
    },

    ignoreDropsOutsideFileInput (event) {
      event.target.type !== 'file' && event.preventDefault()
    },

    generateUniqueFilename () {
      const generatedFilename = uuid.generate()
      this.$set(
        this.fileIconStatus,
        generatedFilename,
        { showFullText: false, loading: true }
      )
      return generatedFilename
    },

    filterFilesBySize ({ files }, maxSizeInMB) {
      const originalFileListLength = files.length
      const filteredFiles = [].filter.call(files, ({ size }) => size < MEGABYTES_IN_BYTES(maxSizeInMB))
      return [filteredFiles, originalFileListLength > filteredFiles.length]
    },

    setImageSrc (file) {
      const reader = new FileReader()
      reader.readAsDataURL(file)

      reader.onloadend = ({ target }) => {
        this.srcImage[file.name] = target.result
      }
    },

    async addFile ({ target }) {
      const MAX_FILE_SIZE = 15

      if (this.onlyImages) {
        Array.prototype.forEach.call(target.files, this.setImageSrc)
      }

      // store current state of files, so we can fallback to that state if newly
      // added files fail to upload
      const fallback = this.value

      let [files, oversizedFileWasRemoved] = this.filterFilesBySize(target, MAX_FILE_SIZE)

      // if the user attempted to attach a file over 15MB, display a warning in the console
      // and flash a warning message below the input field
      if (oversizedFileWasRemoved) {
        this.$emit('warning', this.$t('validation.file_size', { num: MAX_FILE_SIZE }))
        return
      }

      files = files.map(file => {
        const filename = this.generateUniqueFilename()
        return {
          data: file,
          filename,
          displayName: file.name,
          path: this.account.customerId,
          size: file.size,
          type: file.type,
          key: `public/${this.account.customerId}/${filename}`,
          ...this.$s3._config.AWSS3
        }
      })

      // populate the page files array so the ui gets updated appropriately
      this.$emit('input', [].concat(this.value, files))
      try {
        this.$emit('uploading', true)
        // send the files to AWS S3
        await Promise.all(
          files.map(file => this.$s3.put(this.pathToFile(file), file.data))
        )

        this.value.forEach(file => {
          this.fileIconStatus[file.filename].loading = false
          this.$delete(file, 'data')
        })
      } catch (err) {
        // fallback to the state prior to this failed upload
        this.$emit('input', fallback)
        console.log('err:', err)
        this.$emit('warning', this.$t('problems.file_upload'))
      } finally {
        this.$emit('uploading', false)
      }
    },

    pathToFile (file) {
      return `${file.path}/${file.filename}`
    },

    async removeAllFiles (files) {
      const s3DeleteActions = files.map(file => this.$s3.remove(this.pathToFile(file)))
      try {
        await Promise.all(s3DeleteActions)
      } catch (err) {
        console.error(err)
      }
    },

    clearFileInput () {
      // this clears resets the input in the dom so that the same image can be added again after deletion, or on a second form.
      this.$refs['file-input'].value = ''
    },

    async removeFile (file) {
      if (this.fileIconStatus[file.filename] && !this.fileIconStatus[file.filename].loading) {
        this.clearFileInput()
        const fallback = this.value
        let files
        try {
          files = this.value.filter(name => name !== file)
          await this.$s3.remove(this.pathToFile(file))
          this.$delete(this.fileIconStatus, file.filename)
        } catch (err) {
          this.$emit('input', fallback)
          console.log('Error:', err)
        }
        this.$emit('input', files)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.fa-icon-stack {
  margin: 5px 15px;
  font-size: 2.8em !important;

  .upload-icon {
    display: none;
    text-align: center;
  }

  .delete-icon {
    display: none;
    position: absolute;
    top: -70%;
    right: -30%;
    color: red;
    text-shadow: 1px 2px #420e0e;
  }

  .fa-file {
    color: white;
  }

  // if file is uploading, show the upload icon and animate it
  &.loading {
    .upload-icon {
      display: inline;
      animation-duration: 1.2s;
      animation-direction: alternate;
      animation-iteration-count: infinite;
      animation-name: throb;
      animation-timing-function: linear;
    }
  }

  // show the delete icon and animate the file icon to wobble
  // if the icon is hovered over after it has uploaded
  &:not(.loading) {
    &:hover {
      .delete-icon {
        display: inline;
      }

      .fa-file, .img-file {
        animation-duration: 0.4s;
        animation-iteration-count: infinite;
        animation-name: wobble;
        animation-timing-function: ease-in-out;
        transform-origin: 50% 50%;
      }
    }
  }
}

.file-detail-container {
  box-sizing: content-box;
  display: inline-block;
  position: relative;
  max-width: 100px;
  text-align: center;
  margin: 10px 10px;
  vertical-align: middle;

  &:focus-within {
    background-color: rgba(237, 243, 22, 0.4);
  }

  .file-placeholder {
    display: flex;
    width: 70px;
    height: 80px;
    font-size: 12px;
    border: 5px dashed white;
    align-items: center;
    text-align: center;
    padding: 5px;
    justify-content: center;
    background-color: rgba(199, 203, 213, .4);
    color: #385f84;

    &.dragged-over {
      background-color: rgba(237, 243, 22, 0.4);
    }
  }

  &:hover {
    .file-placeholder {
      background-color: rgba(199, 203, 213, 1);
    }
  }

}
.invisible-file-input {
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  bottom: 0;
  opacity: 0;
}

.filename-text {
  font-size: 12px;
  text-overflow: ellipsis;
  height: 14px;
  white-space: nowrap;
  line-height: 14px;
  overflow: hidden;

  &.full {
    overflow: visible;
    line-height: 18px;
    white-space: normal;

    span {
      display: inherit;
      background-color: white;
      overflow-wrap: break-word;
    }
  }
}

.list-item {
  display: inline-block;
  opacity: 1;
}

.list-enter-active, .list-leave-active {
  transition: all 1s;
}

.list-enter, .list-leave-to {
  opacity: 0;
  transition: all .7s;
}

.loading {
  opacity: .4;
}

.form {
  background-color: rgba(34, 127, 207, .3);
  box-shadow: none;
  margin: 20px 0;
  margin-top: 0;
  padding: 0px 10px 30px 10px;

  .form-section {
    padding: 20px 15px;
    // background-color: rgba(255,255,255, .3);
    color:black;
    margin: 0px auto;
    max-width: 1000px;

    &.form-instruction {
      font-size: 1.2em;
    }

    &.dark {
      background-color: #385f84;
      color: white;
    }
  }
}

.image-container {
  height: 85px;
  display: flex;
  align-items: center;
  justify-content: center;

  .image-wrapper {
    position: relative;
  }

  .fa-stack {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .delete-icon {
    display: none;
    position: absolute;
    top: -90%;
    right: -90%;
    color: red;
    text-shadow: 1px 2px #420e0e;
  }

  .loading {
    .upload-icon {
      position: absolute;
      // top: 50%;
      // right: 50%;
    }

    .image-wrapper {
      display: block;
      width: 100px;
      background-color: black;
    }
  }

  .img-file {
    max-height: 60px;
    max-width: 100px;
  }
}

</style>
